import React, { useEffect } from 'react';
import moment from "moment";
import InputRowGroup from "./Inputs/InputRowGroup";
import InputRow from "./Inputs/InputRow";
import DateInput from "./Inputs/DateInput";
import { handleChange } from "../Utils/DataCollectionUtils";
import { useTranslation } from "react-i18next";
import BooleanInput from "./Inputs/BooleanInput";
import DropdownInput from "./Inputs/DropdownInput";
import TextInput from "./Inputs/TextInput";
import NumberInput from "./Inputs/NumberInput";
import QuoteAllModes from "../Quotes/QuoteAllModes";

import {
    DEPOSIT_FREQUENCIES_FOR_QUOTE,
    isCLBOnlyAllowed,
    isGroupPlanAvailable,
    PLAN_TYPE_IDS,
    yearsToMaturity,
    maxCarryForwardGrantRoom,
    isCompletionInsuranceAllowed,
    isBeneficiaryInsuranceAllowed,
    isCriticalIllnessInsuranceAllowed,
    calculateLifetimeDepositAmount,
    PLAN2_TYPES,
    stateWithPlan2AsPrimary, depositModesForPlanTypeTranslated, calculateMonthlyDepositAmount, incomeWarning
} from '../Utils/PlanUtils';
import { displayCurrency, displayUnits, sprintf } from "../Utils/FormattingUtils";
import { calculateMaxEducationSavingsPlans } from "../Utils/KYCUtils";


const SecondPlanDataCollection = ({ state }) => {
    const { i18n } = useTranslation();
    const { t } = useTranslation();
    const [quoting, setQuoting] = React.useState(false);

    const [maturityDescription, setMaturityDescription] = React.useState('');

    //Update the maturity description string based on the maturity date and beneficiary birthdate
    useEffect(() => {
        const beneficiaryAgeInMaturityYear = moment(state.plan2.maturityDate, "YYYY-MM-DD").year() - moment(state.beneficiary.birthdate, "YYYY-MM-DD").year();
        if (beneficiaryAgeInMaturityYear < 0 || isNaN(beneficiaryAgeInMaturityYear)) {
            setMaturityDescription(``);
        } else if (beneficiaryAgeInMaturityYear <= 18) {
            const name = state.beneficiary.firstName || 'the beneficiary';
            setMaturityDescription(sprintf(t("Plan_Mature"), moment(state.plan2.maturityDate).format("MMMM Do, YYYY"), name, beneficiaryAgeInMaturityYear));
        }

    }, [state.plan2.maturityDate, state.beneficiary.birthdate]);

    const planOptions = PLAN2_TYPES.filter((plan) => {

        // same as plan1, except if plan 1 is a Group T mode we don't allow Group int he second plan
        if (plan.value === state.plan.planTypeID && !(state.plan.planTypeID === PLAN_TYPE_IDS.GROUP && state.plan.depositMode === 'T')) {
            return true;
        }

        if (plan.value === -1) { // none
            return true;
        }

        if (plan.value === 100) { // transfer
            return true;
        }

        return false;
    });

    const depositCodeOptions = Array.from({ length: yearsToMaturity(state) }, (_, i) => i + 1).map((i) => ({
        label: i,
        value: i
    }));

    const viewQuote = () => {
        setQuoting(true);
    }

    const stopViewingQuote = () => {
        setQuoting(false);
    }


    if (quoting) {
        // When the Generate Quotes button is pressed, we switch into quote display mode
        return (
            <div>
                <QuoteAllModes
                    state={stateWithPlan2AsPrimary(state)}
                    showAllSimultaneously={false}
                    backFunction={stopViewingQuote}
                />
            </div>
        )
    } else {
        //When we're not viewing a quote, we're collecting data for the second plan
        return (
            <form>
                <InputRowGroup label={t('Plan Selection')}>
                    <InputRow label={t('Plan Type')}>
                        <DropdownInput
                            name={'planTypeID'}
                            state={state.plan2}
                            onChange={handleChange(state.setPlan2)}
                            options={planOptions}
                            numberValue={true}
                        />

                    </InputRow>
                </InputRowGroup>

                {state.plan2.planTypeID !== 100 && state.plan2.planTypeID >= 0 &&
                    <InputRowGroup label={t('Plan Information')}>
                        <InputRow label={t('Deposit Mode')}>
                            <DropdownInput
                                name={'depositMode'}
                                state={state.plan2}
                                onChange={handleChange(state.setPlan2)}
                                options={depositModesForPlanTypeTranslated(state.plan2.planTypeID)}
                            />
                        </InputRow>

                        <InputRow label={t('Effective Date')}>
                            <DateInput
                                name={'effectiveDate'}
                                state={state.plan2}
                                onChange={handleChange(state.setPlan2)}
                            />
                        </InputRow>

                        {state.plan2.planTypeID === PLAN_TYPE_IDS.GROUP &&
                            <InputRow label={t('Deposit Code')}>
                                <span>{state.plan2.depositCode}</span>
                            </InputRow>
                        }
                        {state.plan2.planTypeID !== PLAN_TYPE_IDS.GROUP &&
                            <InputRow label={t('Deposit Code')}>
                                <DropdownInput
                                    name={'depositCode'}
                                    state={state.plan2}
                                    onChange={handleChange(state.setPlan2)}
                                    options={depositCodeOptions}
                                />
                            </InputRow>
                        }
                        <InputRow label={t('Maturity Date')}>
                            <DateInput
                                name={'maturityDate'}
                                state={state.plan2}
                                onChange={handleChange(state.setPlan2)}
                            />
                        </InputRow>

                        <InputRow>
                            <div className={'centered-text'}>{t(maturityDescription)}</div>
                        </InputRow>

                        {isCLBOnlyAllowed(state) &&
                            <InputRow label={t('CLB Only')}>
                                <BooleanInput
                                    label={t("Yes")}

                                    name={'clbOnly'}
                                    state={state.plan2}
                                    onChange={handleChange(state.setPlan2)}
                                />
                            </InputRow>
                        }

                        {state.plan2.planTypeID === PLAN_TYPE_IDS.GROUP &&
                            <InputRow label={t('Quote Based On')}>
                                <DropdownInput
                                    name={'quoteBasedOn'}
                                    state={state.quote2}
                                    onChange={handleChange(state.setQuote2)}
                                    options={[
                                        { label: t('Number of Units'), value: 'units' },
                                        { label: t('Deposit Amount'), value: 'deposit' }
                                    ]}
                                />
                            </InputRow>
                        }


                        {state.plan2.planTypeID === 0 &&
                            <InputRow label={t('Number of Units')}>
                                {state.quote2.quoteBasedOn === 'units' &&
                                    <NumberInput
                                        name={'numberOfUnits'}
                                        state={state.plan2}
                                        onChange={handleChange(state.setPlan2)}
                                        validationRules={{
                                            valueAsNumber: true,
                                            validate: (value) => value >= 0,
                                        }}
                                        disabled={+(state.plan2.planTypeID) !== PLAN_TYPE_IDS.GROUP}
                                    />
                                }
                                {state.quote2.quoteBasedOn === 'deposit' &&
                                    <span>{!isNaN(state.plan2.numberOfUnits) && displayUnits(state.plan2.numberOfUnits)}</span>
                                }
                            </InputRow>
                        }

                        <InputRow
                            label={state.plan2.planTypeID === PLAN_TYPE_IDS.ACHIEVERS ? t('Plan Contribution Target') : t('Deposit Amount')}>
                            {state.quote2.quoteBasedOn === 'deposit' &&
                                <NumberInput
                                    label={t('\$')}
                                    name={'depositAmount'}
                                    state={state.plan2}
                                    onChange={handleChange(state.setPlan2)}
                                    maxLength={8}
                                    validationRules={{
                                        valueAsNumber: true,
                                        validate: (value) => value >= 0,
                                    }}
                                />
                            }
                            {state.quote2.quoteBasedOn === 'units' &&
                                <span>{!isNaN(state.plan2.depositAmount) && displayCurrency(state.plan2.depositAmount, i18n.language, 2)} </span>
                            }

                            {+(state.plan2.planTypeID) !== PLAN_TYPE_IDS.ACHIEVERS &&
                                <DropdownInput
                                    name={'depositFrequency'}
                                    state={state.quote2}
                                    onChange={handleChange(state.setQuote2)}
                                    options={DEPOSIT_FREQUENCIES_FOR_QUOTE}
                                />
                            }
                        </InputRow>

                        {state.plan2.planTypeID === PLAN_TYPE_IDS.ACHIEVERS &&
                            <InputRow label={t('Insurance')}>
                                <span>{displayCurrency(calculateLifetimeDepositAmount(state, true) - calculateLifetimeDepositAmount(state, false), i18n.language, 2)}</span>
                            </InputRow>
                        }
                        {state.plan2.planTypeID === PLAN_TYPE_IDS.ACHIEVERS &&
                            <InputRow label={t('Total Contributions')}>
                                <span>{displayCurrency(calculateLifetimeDepositAmount(state, true), i18n.language, 2)}</span>
                            </InputRow>
                        }

                        {maxCarryForwardGrantRoom(state) > 0 &&
                            <InputRow label={t('Carry Forward Grant Room')}>
                                <NumberInput
                                    label={t('\$')}
                                    name={'grantRoom'}
                                    state={state.plan2}
                                    onChange={handleChange(state.setPlan2)}
                                    validationRules={{
                                        valueAsNumber: true,
                                        validate: (value) => value >= 0,
                                    }}
                                />
                                <span
                                    className={'info'}>{`${t('Maximum')} $${maxCarryForwardGrantRoom(state)}`}</span>
                            </InputRow>
                        }

                        {calculateLifetimeDepositAmount(stateWithPlan2AsPrimary(state), false) > 50000 &&
                            <InputRow>
                                <div
                                    className={'financial-warning'}>{t('Lifetime_Limit')}</div>
                            </InputRow>
                        }


                        {calculateLifetimeDepositAmount(stateWithPlan2AsPrimary(state), false) <= 50000 &&
                            (state.plan2.planTypeID === PLAN_TYPE_IDS.GROUP ?
                            calculateMaxEducationSavingsPlans(state.subscriber, state.jointSubscriber, state.knowYourClient).monthlyMaximumEducationSavingsPlanGroup :
                            calculateMaxEducationSavingsPlans(state.subscriber, state.jointSubscriber, state.knowYourClient).monthlyMaximumEducationSavingsPlan)
                            < calculateMonthlyDepositAmount(stateWithPlan2AsPrimary(state), true) && (
                                <InputRow>
                                    <div
                                        className={'financial-warning'}>{t('KYC_Limit')}</div>

                                </InputRow>
                            )
                        }

                        {calculateLifetimeDepositAmount(stateWithPlan2AsPrimary(state), false) <= 50000 &&
                            (state.plan2.planTypeID === PLAN_TYPE_IDS.GROUP ?
                            calculateMaxEducationSavingsPlans(state.subscriber, state.jointSubscriber, state.knowYourClient).monthlyMaximumEducationSavingsPlanGroup :
                            calculateMaxEducationSavingsPlans(state.subscriber, state.jointSubscriber, state.knowYourClient).monthlyMaximumEducationSavingsPlan)
                            >= calculateMonthlyDepositAmount(stateWithPlan2AsPrimary(state), true) &&
                            incomeWarning(stateWithPlan2AsPrimary(state)) && (
                                <InputRow>
                                    <div
                                        className={'financial-warning'}>{t('Affordability_Limit')}
                                    </div>
                                </InputRow>
                            )
                        }


                    </InputRowGroup>
                }
                {state.plan2.planTypeID >= 0 &&
                    <InputRowGroup label={t('Insurance Options')}>
                        <InputRow>
                            <div>{t("Insurance options must be configured on the first plan")}</div>
                        </InputRow>
                    </InputRowGroup>

                }


                {state.plan2.planTypeID >= 0 && state.plan2.planTypeID !== PLAN_TYPE_IDS.TRANSFER &&
                    <InputRowGroup>
                        <InputRow>
                            {/*}<button onClick={viewQuote}>{t("Generate Quotation")}</button>{*/}
                        </InputRow>
                    </InputRowGroup>
                }
            </form>
        )
    }
}

export default SecondPlanDataCollection;